import { useEffect, useState, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Application } from "../../app/stores/applications-slice";
import { useApplications } from "../../hooks/use-applications";
import { Chrome as ChromeWindow } from "../applications/Chrome";
import { Readme } from "../applications/Readme";
import { Settings as SettingsWindow } from "../applications/Settings";
import { YoutubeViewer } from "../applications/youtube-viewer/youtube-viewer";
import { LogMonitor } from "../applications/log-monitor/log-monitor";
import { useWindow } from "../DraggableWindow/context/WindowContext";
import { DraggableWindow } from "../DraggableWindow/index";
import FileExplorer from "../FileExplorer";
import StartMenu from "../StartMenu";
import Taskbar from "../Taskbar";
import { DesktopContextMenu } from "./components/DesktopContextMenu";
import { DesktopShortcut } from "./desktop-shortcut";

// Define the grid cell interface
interface GridCell {
    id: string;
    x: number;
    y: number;
    application?: Application;
}

// Define the drag item type
const ItemTypes = {
    SHORTCUT: "shortcut",
};

// Grid cell component
const GridCellComponent: React.FC<{
    cell: GridCell;
    moveShortcut: (fromX: number, fromY: number, toX: number, toY: number, applicationId: string) => void;
    onDoubleClick?: () => void;
}> = ({ cell, moveShortcut, onDoubleClick }) => {
    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.SHORTCUT,
        item: { x: cell.x, y: cell.y, applicationId: cell.application?.id },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.SHORTCUT,
        drop: (item: { x: number; y: number; applicationId: string }) => {
            if (item.x !== cell.x || item.y !== cell.y) {
                moveShortcut(item.x, item.y, cell.x, cell.y, item.applicationId);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    return (
        <div
            ref={(node) => drag(drop(node))}
            className={`desktop-grid-cell ${isOver ? "bg-white/10" : ""} ${isDragging ? "opacity-50" : ""}  rounded-lg transition-colors`}
            style={{
                width: "80px",
                height: "80px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>
            {cell.application && <DesktopShortcut application={cell.application} onDoubleClick={onDoubleClick} />}
        </div>
    );
};

export const DesktopContent = () => {
    const { windows, openWindow, closeWindow, minimizeWindow, bringToFront } = useWindow();
    const [showStartMenu, setShowStartMenu] = useState(false);
    const { applications, updateApplicationGridPosition } = useApplications();

    // Calculate grid dimensions based on screen size and cell size
    const cellSize = 100; // 80px + 20px gap
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight - 48; // Subtract taskbar height
    const columns = Math.floor(screenWidth / cellSize);
    const rows = Math.floor(screenHeight / cellSize);

    // Initialize grid cells
    const [gridCells, setGridCells] = useState<GridCell[]>(() => {
        const cells: GridCell[] = [];
        for (let y = 0; y < rows; y++) {
            for (let x = 0; x < columns; x++) {
                cells.push({
                    id: `cell-${x}-${y}`,
                    x,
                    y,
                });
            }
        }

        // Place applications in their saved positions
        applications.forEach((app: any) => {
            const gridPosition = app.gridPosition || { x: 0, y: 0 };
            const cellIndex = gridPosition.y * columns + gridPosition.x;
            if (cellIndex < cells.length) {
                cells[cellIndex].application = app;
            }
        });

        return cells;
    });

    // Handle shortcut movement
    const moveShortcut = useCallback(
        async (fromX: number, fromY: number, toX: number, toY: number, applicationId: string) => {
            // Update the grid cells immediately for smooth UI
            setGridCells((prevCells) => {
                const newCells = [...prevCells];
                const fromIndex = fromY * columns + fromX;
                const toIndex = toY * columns + toX;
                const app = newCells[fromIndex].application;
                newCells[fromIndex].application = undefined;
                newCells[toIndex].application = app;
                return newCells;
            });

            // Update the position in IndexedDB
            await updateApplicationGridPosition(applicationId, toX, toY);
        },
        [columns, updateApplicationGridPosition]
    );

    const handleShortcutDoubleClick = (application: Application) => {
        if (application.path === "/file-explorer") {
            openWindow("explorer", "File Explorer", <FileExplorer />);
        } else if (application.path === "/chrome") {
            openWindow("chrome", "Google Chrome", <ChromeWindow />);
        } else if (application.path === "/settings") {
            openWindow("settings", "Settings", <SettingsWindow />);
        } else if (application.path === "/readme") {
            openWindow("readme", "Readme.txt", <Readme />);
        } else if (application.path === "/youtube-viewer") {
            openWindow("youtube-viewer", "Youtube Viewer", <YoutubeViewer />);
        } else if (application.path === "/log-monitor") {
            openWindow("log-monitor", "🚨 Logs Monitoring", <LogMonitor />);
        }
    };

    const toggleStartMenu = () => {
        setShowStartMenu(!showStartMenu);
    };

    // Update grid when applications change
    useEffect(() => {
        const hasApplicationsChanged = applications.some((app, index) => {
            const currentCell = gridCells.find((cell) => cell.application?.id === app.id);
            const currentPosition = currentCell ? { x: currentCell.x, y: currentCell.y } : null;
            const newPosition = app.gridPosition || { x: 0, y: 0 };
            return !currentPosition || currentPosition.x !== newPosition.x || currentPosition.y !== newPosition.y;
        });

        if (hasApplicationsChanged) {
            setGridCells((prevCells) => {
                const newCells = prevCells.map((cell) => ({ ...cell, application: undefined }));
                applications.forEach((app: any) => {
                    const gridPosition = app.gridPosition || { x: 0, y: 0 };
                    const cellIndex = gridPosition.y * columns + gridPosition.x;
                    if (cellIndex < newCells.length) {
                        newCells[cellIndex].application = app;
                    }
                });
                return newCells;
            });
        }
    }, [applications, columns, gridCells]);

    // Close start menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (showStartMenu && !target.closest("#start-menu") && !target.closest("#taskbar-start-button")) {
                setShowStartMenu(false);
            }
        };

        document.addEventListener("mousemove", handleClickOutside);
        return () => {
            document.removeEventListener("mousemove", handleClickOutside);
        };
    }, [showStartMenu]);

    return (
        <DndProvider backend={HTML5Backend}>
            <div
                className="h-screen w-screen overflow-hidden bg-gradient-to-br from-blue-500 to-blue-700 relative"
                onClick={() => showStartMenu && setShowStartMenu(false)}>
                <div
                    className="desktop-grid"
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 48, // Leave space for taskbar
                        padding: "20px",
                        display: "grid",
                        gridTemplateColumns: `repeat(${columns}, ${cellSize}px)`,
                        gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
                        gap: "20px",
                    }}>
                    {gridCells.map((cell) => (
                        <GridCellComponent
                            key={cell.id}
                            cell={cell}
                            moveShortcut={moveShortcut}
                            onDoubleClick={cell.application ? () => handleShortcutDoubleClick(cell.application!) : undefined}
                        />
                    ))}
                </div>

                {/* Windows */}
                {windows.map(
                    (window) =>
                        !window.isMinimized && (
                            <DraggableWindow
                                key={window.id}
                                window={window}
                                onClose={() => closeWindow(window.id)}
                                onMinimize={() => minimizeWindow(window.id)}
                                bringToFront={() => bringToFront(window.id)}
                            />
                        )
                )}

                {/* Start Menu */}
                {showStartMenu && <StartMenu onAppClick={openWindow} />}

                {/* Taskbar */}
                <Taskbar
                    onStartClick={toggleStartMenu}
                    windows={windows}
                    onWindowClick={(id) => {
                        const window = windows.find((w) => w.id === id);
                        if (window?.isMinimized) {
                            openWindow(window.id, window.title, window.component);
                        }
                    }}
                />

                {/* Context Menu */}
                <DesktopContextMenu />
            </div>
        </DndProvider>
    );
};
